import BaseProductTier from 'developer-docs-mdx-components/components/ProductTier/BaseProductTier';
import ClickableHeader from 'developer-docs-shared-components/modules/Header/ClickableHeader';
import RelatedDocs from 'developer-docs-shared-components/components/RelatedDocs';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { useViewport } from 'developer-docs-shared-components/hooks/useViewport';
import styled from 'styled-components';
import { CARD_BOX_SHADOW } from 'developer-docs-shared-components/config/cssConstants';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo } from 'react';
import { introductionId, useCasesId } from './constants';
import { publicApiSpecProductTierRequirementsToProductTierProps } from './util';
import { isUrlLocalized } from '../../utils/localization';
import { getEnglishBaseUrl } from '../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// We need the border radius here.
const StyledCard = styled.div.withConfig({
  displayName: "APIHeaderCard__StyledCard",
  componentId: "m7rmio-0"
})(["background-color:", ";border-radius:12px;box-shadow:", ";inline-size:", ";margin-block:", ";"], ({
  theme
}) => theme.color['fill-surface-default'], CARD_BOX_SHADOW, ({
  isAboveColumn
}) => isAboveColumn ? '100%' : 'auto', ({
  isAboveColumn,
  theme
}) => isAboveColumn ? theme.space[800] : 0);
const H2 = props => /*#__PURE__*/_jsx(Text, Object.assign({
  typography: "heading-400",
  as: "h2",
  color: "temp-do-not-use-specialty-scrim"
}, props));
const H3 = props => /*#__PURE__*/_jsx(Text, Object.assign({
  typography: "heading-400",
  as: "h3",
  color: "temp-do-not-use-specialty-scrim"
}, props));
const APIHeaderCard = ({
  version
}) => {
  var _version$introContent4;
  const viewport = useViewport();
  const {
    isAboveColumn,
    isBetweenMobileAndColumn
  } = viewport;
  const intro = useMemo(() => {
    var _version$introContent;
    return version.isLegacy ? version.intro : (_version$introContent = version.introContent) === null || _version$introContent === void 0 ? void 0 : _version$introContent.intro;
  }, [version]);
  const useCase = useMemo(() => {
    var _version$introContent2;
    return version.isLegacy ? version.useCase : (_version$introContent2 = version.introContent) === null || _version$introContent2 === void 0 ? void 0 : _version$introContent2.useCase;
  }, [version]);
  const sharedLinkProps = useMemo(() => {
    return {
      localization: {
        isUrlLocalized,
        getEnglishBaseUrl
      },
      relatedDocType: 'guide'
    };
  }, []);
  const publicApiSpecProductTierRequirements = useMemo(() => {
    var _version$introContent3;
    return !version.isLegacy && ((_version$introContent3 = version.introContent) === null || _version$introContent3 === void 0 ? void 0 : _version$introContent3.requirements) && publicApiSpecProductTierRequirementsToProductTierProps(version.introContent.requirements);
  }, [version]);
  if (!(intro || useCase)) {
    return null;
  }
  return (
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized */
    _jsx(StyledCard, {
      isAboveColumn: isAboveColumn,
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: isAboveColumn ? 'row' : 'column',
        justifyContent: "space-between",
        padding: "600",
        children: [/*#__PURE__*/_jsxs(View, {
          maxInlineSize: "800px",
          inlineSize: isAboveColumn ? '60%' : '100%',
          children: [intro && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(ClickableHeader, {
              Component: H2,
              id: introductionId,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "developer-docs-ui.apiReference.introductionTitle"
              })
            }), /*#__PURE__*/_jsx(Text, {
              as: "p",
              typography: "body-200",
              marginBlockStart: "400",
              marginBlockEnd: "800",
              children: intro
            })]
          }), useCase && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(ClickableHeader, {
              Component: H3,
              id: useCasesId,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "developer-docs-ui.apiReference.useCasesTitle"
              })
            }), /*#__PURE__*/_jsx(Text, {
              as: "p",
              typography: "body-200",
              marginBlockStart: "400",
              marginBlockEnd: "800",
              children: useCase
            })]
          }), version.isLegacy ? version.relatedDocuments && /*#__PURE__*/_jsx(RelatedDocs, Object.assign({
            title: version.relatedDocuments.title,
            href: version.relatedDocuments.href
          }, sharedLinkProps)) : ((_version$introContent4 = version.introContent) === null || _version$introContent4 === void 0 ? void 0 : _version$introContent4.relatedDocumentation[0]) && /*#__PURE__*/_jsx(RelatedDocs, Object.assign({
            href: version.introContent.relatedDocumentation[0].url,
            title: version.introContent.relatedDocumentation[0].name
          }, sharedLinkProps))]
        }), publicApiSpecProductTierRequirements && publicApiSpecProductTierRequirements.length && /*#__PURE__*/_jsx(View, {
          as: "aside",
          inlineSize: isAboveColumn ? '30.7%' : '100%',
          marginBlockStart: isBetweenMobileAndColumn ? '800' : '0',
          "aria-label": I18n.text('developer-docs-mdx-components.productTier.header'),
          children: /*#__PURE__*/_jsx(BaseProductTier, {
            tiers: publicApiSpecProductTierRequirements
          })
        })]
      })
    })
  );
};
export default APIHeaderCard;